import { ApplicationConfig, provideZoneChangeDetection, isDevMode, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { apiKeyInterceptor } from '@app/utils/interceptors/api-key.interceptor';
import { serverErrorInterceptor } from '@app/utils/interceptors/server-error.interceptor';
import { globalLoaderInterceptor } from '@app/utils/interceptors/global-loader.interceptor';
import { backOfficeAuthInterceptor } from './backOffice/interceptors/back-office-auth.interceptor';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { ConfigService } from './core/config/config.service';
import { ThemeService } from './core/theme/theme.service';
import { TenantThemeService } from './core/theme/tenant-theme.service';
import { LanguageService } from './core/i18n/language.service';
import { PostalCodeRepository } from './domain/ports/postal-code.repository';
import { PostalCodeLocalDataSource } from './data/data-sources/local/postal-code-local.data-source';
// Alternativa HTTP comunitaria; se mantiene importable por si se quisiera swap.
// import { PostalCodeRemoteDataSource } from './data/data-sources/remote/postal-code-remote.data-source';
import { KybRepository } from './domain/ports/kyb.repository';
import { KybApiService } from './onboarding/kyb/services/kyb-api.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([globalLoaderInterceptor, apiKeyInterceptor, backOfficeAuthInterceptor, serverErrorInterceptor])),
    provideTransloco({
      config: {
        availableLangs: ['es', 'en', 'pt'],
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        languageService: LanguageService,
        themeService: ThemeService,
        tenantThemeService: TenantThemeService
      ) => async () => {
        await configService.load();
        themeService.loadCachedTheme();
        await Promise.all([
          languageService.init(),
          tenantThemeService.loadTenantTheme(),
        ]);
      },
      deps: [ConfigService, LanguageService, ThemeService, TenantThemeService],
      multi: true
    },
    // Domain ports → data sources. Self-hosted: el dataset SEPOMEX vive en
    // `/assets/sepomex.json` (lazy-loaded en el primer lookup, cacheado en
    // memoria). Cero dependencia externa, cero CORS, cero rate limits.
    // Para regenerar el dataset desde un dump oficial: `npm run sepomex:build
    // <input.csv>`. Para volver al provider HTTP comunitario, swapear a
    // `PostalCodeRemoteDataSource`.
    { provide: PostalCodeRepository, useClass: PostalCodeLocalDataSource },
    // KybRepository → KybApiService (puente actual). Componentes nuevos deben
    // inyectar el puerto abstracto; el bridge mock/remote sigue dentro de la
    // impl mientras se prepara el split en data-sources separadas.
    { provide: KybRepository, useExisting: KybApiService },
  ]
};
